import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, isRef as _isRef, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col span-6" }
const _hoisted_4 = { class: "row" }
const _hoisted_5 = { class: "col span-6" }
const _hoisted_6 = {
  key: 1,
  class: "row"
}
const _hoisted_7 = { class: "col span-6" }

import { useStore } from 'vuex';
import { ref, computed, reactive, onMounted, watch } from 'vue';

import { 
  EPINIO_TYPES, 
  EpinioNamespace, 
  EpinioCatalogService,
} from '../types';
import ServiceInstance from '../models/services';
import { objValuesToString } from '../utils/settings';
import { useEpinioBindAppsMixin } from './bind-apps-mixin';
import { epinioExceptionToErrorsArray } from '../utils/errors';
import ChartValues from '../components/settings/ChartValues.vue';
import EpinioCatalogServiceModel from '../models/catalogservices';

import Banner from '@components/Banner/Banner.vue';
import Loading from '@shell/components/Loading.vue';
import CruResource from '@shell/components/CruResource.vue';
import LabeledSelect from '@shell/components/form/LabeledSelect.vue';
import NameNsDescription from '@shell/components/form/NameNsDescription.vue';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';

import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';


export default /*@__PURE__*/_defineComponent({
  __name: 'services',
  props: {
    value: {},
    initialValue: {},
    mode: {}
  },
  setup(__props: any) {

const store = useStore();
const t = store.getters['i18n/t'];

const props = __props;

const { 
  selectedApps, 
  nsAppOptions, 
  noApps, 
  updateServiceInstanceAppBindings,
} = useEpinioBindAppsMixin(props);

const doneRoute = ref<string>('');
const pending = ref<boolean>(true);
const errors = ref<Array<string>>([]);
const doneParams = reactive<object>({});
const validChartValues = ref<object>({});
const failedWaitingForServiceInstance = ref<boolean>(false);
const chartValues = reactive<ChartValues>(objValuesToString(props.value?.settings) || {});

onMounted(async () => {
  await Promise.all([
    store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CATALOG_SERVICE }),
    store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP }),
  ]);

  pending.value = false;

  //Needed doneParams/doneRoute but the mixin that generates these is a rancher
  //shell mixin, copied parts needed, will move to composable function as needed.
  if (props.value?.doneParams) {
    return props.value.doneParams;
  }

  const out = { ...store.$router.currentRoute._value.params };

  delete out.namespace;
  delete out.id;

  doneParams.value = out;

  if (props.value?.doneRoute ) {
    doneRoute.value = props.value.doneRoute;
  }else{
    let name = props.route.name;

    if ( name?.endsWith('-id') ) {
      name = name.replace(/(-namespace)?-id$/, '');
    } else if ( name?.endsWith('-create') ) {
      name = name.replace(/-create$/, '');
    }

    doneRoute.value = name;
  }
})

watch(
  () => props.value.meta.namespace,
  () => {
    return selectedApps.value = [];
  }
);

const catalogServices = computed(() => {
  return store.getters['epinio/all'](EPINIO_TYPES.CATALOG_SERVICE);
});

const selectedCatalogService = computed(() => {
  return catalogServices.value?.find(
    ({id}: EpinioCatalogServiceModel) => id === props.value.catalog_service)
  ;
});

const isEdit = computed(() => {
  return props.mode === 'edit';
});

const validationPassed = computed(() => {
  if (isEdit.value && newBinds.value) {
    return true;
  }
  
  if (!props.value.catalog_service) {
    return false;
  }

  if (!Object.values(validChartValues.value).every((v) => !!v)) {
    return false;
  }

  const nameErrors = validateKubernetesName(
    props.value.name || '', 
    t('epinio.namespace.name'), 
    store.getters, 
    undefined, 
    [],
  );
  const nsErrors = validateKubernetesName(
    props.value.meta.namespace || '', 
    '', 
    store.getters, 
    undefined, 
    [],
  );

  if (nameErrors.length === 0 && nsErrors.length === 0) {
    return !failedWaitingForServiceInstance.value;
  }

  return false;
});

const namespaces = computed(() => {
  return sortBy(store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE), 'name');
});

const namespaceNames = computed(() => {
  return namespaces.value.map((n: EpinioNamespace) => n.metadata.name);
});

const catalogServiceOpts = computed(() => {
  return catalogServices.value.map((cs: EpinioCatalogService) => ({
    label: `${cs.name} (${cs.short_description})`,
    value: cs.name,
  }));
});

const noCatalogServices = computed(() => {
  return catalogServices.value.length === 0;
});

const newBinds = computed(() => {
  return !isEqual(sortBy(selectedApps.value), sortBy(props.value.boundapps));
});

const showChartValues = computed(() => {
  return Object.keys(selectedCatalogService.value?.settings || {}).length !== 0;
});

const done = () => {
  if (!doneRoute.value) {
    return;
  }

  store.$router.replace({
    name:   doneRoute.value,
    params: doneParams.value || { resource: props.value.type },
  });
}

const save = async (saveCb: (success: boolean) => void) => {
  errors.value = [];

  const newSettings = !isEqual(
    objValuesToString(chartValues), 
    objValuesToString(props.value.settings),
  );
  
  if (newSettings) {
    props.value.settings = objValuesToString(chartValues.value);
  }else{
    props.value.settings = undefined;
  }

  try {
    if (!isEdit.value) {
      await props.value.create();
      if (selectedApps.value.length) {
        await updateServiceInstanceAppBindings(props.value);
      }
      await store.dispatch(
        'epinio/findAll', 
        { type: props.value.type, opt: { force: true }},
      );

      saveCb(true);
      done();
    }

    if (isEdit.value) {
      if (newSettings) {
        await props.value.update();
      }
      await updateServiceInstanceAppBindings(props.value);
      await props.value.forceFetch();
       
      saveCb(true);
      done();

      //Can't find exactly how this is coming in, seems like a k8s attribute
      //if (!_isBeingDestroyed || !_isDestroyed) {
      //}
    }
  } catch (err: Error | any) {
    if (err.message === 'waitingForServiceInstance') {
      failedWaitingForServiceInstance.value = true;
      errors.value = [t('epinio.serviceInstance.create.catalogService.failedWaitingForServiceInstance')];
    } else {
      errors.value = epinioExceptionToErrorsArray(err);
    }
    saveCb(false);
  }
};

const resetChartValues = () => {
  chartValues.value = {};
  validChartValues.value = {};
};

return (_ctx: any,_cache: any) => {
  return (!_ctx.value || pending.value)
    ? (_openBlock(), _createBlock(Loading, { key: 0 }))
    : (_ctx.value)
      ? (_openBlock(), _createBlock(CruResource, {
          key: 1,
          "can-yaml": false,
          "done-route": doneRoute.value,
          mode: _ctx.mode,
          "validation-passed": validationPassed.value,
          resource: _ctx.value,
          errors: errors.value,
          "namespace-key": "meta.namespace",
          onFinish: save,
          onErrors: _cache[4] || (_cache[4] = e=>errors.value = e)
        }, {
          default: _withCtx(() => [
            (errors.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
                    return (_openBlock(), _createBlock(Banner, {
                      key: i,
                      color: "error",
                      label: err
                    }, null, 8, ["label"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _cache[7] || (_cache[7] = _createTextVNode()),
            _createVNode(NameNsDescription, {
              "name-key": "name",
              "namespace-key": "namespace",
              "namespaces-override": namespaceNames.value,
              "create-namespace-override": true,
              "description-hidden": true,
              value: _ctx.value.meta,
              mode: _ctx.mode
            }, null, 8, ["namespaces-override", "value", "mode"]),
            _cache[8] || (_cache[8] = _createTextVNode()),
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(LabeledSelect, {
                  value: _ctx.value.catalog_service,
                  "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((_ctx.value.catalog_service) = $event)),
                  loading: pending.value,
                  options: catalogServiceOpts.value,
                  disabled: pending.value || isEdit.value,
                  searchable: true,
                  mode: _ctx.mode,
                  multiple: false,
                  "label-key": 'epinio.serviceInstance.create.catalogService.label',
                  placeholder: pending.value || noCatalogServices.value ? _unref(t)('epinio.serviceInstance.create.catalogService.placeholderNoOptions') : _unref(t)('epinio.serviceInstance.create.catalogService.placeholderWithOptions'),
                  required: "",
                  "onOption:selected": resetChartValues
                }, null, 8, ["value", "loading", "options", "disabled", "mode", "placeholder"])
              ])
            ]),
            _cache[9] || (_cache[9] = _createTextVNode()),
            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "spacer" }, null, -1)),
            _cache[11] || (_cache[11] = _createTextVNode()),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(LabeledSelect, {
                  value: _unref(selectedApps),
                  "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => (_isRef(selectedApps) ? (selectedApps).value = $event : null)),
                  loading: pending.value,
                  options: _unref(nsAppOptions),
                  disabled: _unref(noApps) || pending.value,
                  searchable: true,
                  mode: _ctx.mode,
                  multiple: true,
                  "label-key": 'epinio.configurations.bindApps.label',
                  placeholder: pending.value || _unref(noApps) ? _unref(t)('epinio.configurations.bindApps.placeholderNoOptions') : _unref(t)('epinio.configurations.bindApps.placeholderWithOptions')
                }, null, 8, ["value", "loading", "options", "disabled", "mode", "placeholder"])
              ])
            ]),
            _cache[12] || (_cache[12] = _createTextVNode()),
            (showChartValues.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _cache[5] || (_cache[5] = _createElementVNode("div", { class: "spacer" }, null, -1)),
                    _cache[6] || (_cache[6] = _createTextVNode()),
                    _createVNode(ChartValues, {
                      value: chartValues,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((chartValues) = $event)),
                      chart: selectedCatalogService.value.settings,
                      title: _unref(t)('epinio.services.chartValues.title'),
                      mode: _ctx.mode,
                      onValid: _cache[3] || (_cache[3] = ($event: any) => (validChartValues.value = $event))
                    }, null, 8, ["value", "chart", "title", "mode"])
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1,
          __: [7,8,9,10,11,12]
        }, 8, ["done-route", "mode", "validation-passed", "resource", "errors"]))
      : _createCommentVNode("", true)
}
}

})